import { getEnv, types as t } from 'mst-effect';
import uniqBy from 'lodash/uniqBy';
import Router from 'next/router';
import { mapOr } from 'utils/fp';
import { Dataset } from 'api/grpc/resources';
import { withFetchAllPages } from 'utils/requestHelpers/withFetchAllPages';
import { withRunInAction } from 'utils/withRunInAction';

export interface DatasetFilter {
  id: string;
  negate: boolean;
}

/**
 * Store that manages storing datasets, fetching and selecting them
 */
export const DatasetFiltersStore = t
  .compose(
    withRunInAction(),
    t.model('DatasetFiltersStore', {
      /** All loaded datasets */
      datasets: t.frozen<Dataset[]>(),

      /** Currently selected datasets to filter */
      datasetFilters: t.array(
        t.model({
          id: t.string,
          negate: t.boolean,
        }),
      ),

      /** Indicates if all datasets have been fetched */
      allDatasetsFetched: t.optional(t.boolean, false),

      /** Indicates if user has selected to show unassigned datasets */
      shouldShowUnassigned: t.optional(t.boolean, false),
    }),
    withFetchAllPages(),
  )
  .views((self) => ({
    // gets overridden so technically lacks coverage
    // istanbul ignore next
    get env() {
      return getEnv<CF.MSTEnvironment>(self);
    },

    /**
     * All loaded dataset IDs but only if shouldShowUnassigned is set to true
     */
    get unassignedFilter() {
      const hasBeenFetched = self.shouldShowUnassigned && self.allDatasetsFetched;

      if (hasBeenFetched) {
        const datasetsIds = self.datasets.map((dataset) => dataset.getId());
        return uniqBy<string>(datasetsIds, (id) => id);
      }

      return [];
    },
  }))
  .actions((self) => ({
    setDatasets(datasets: Dataset[]) {
      self.datasets = datasets;
    },

    clearDatasetFilters() {
      self.runInAction(() => {
        self.datasetFilters.replace([]);
      });
    },

    getDatasetFilter(datasetId: string) {
      return self.datasetFilters.find(({ id }) => id === datasetId);
    },

    setAllDatasetsFetched: (fetched: boolean) => {
      self.allDatasetsFetched = fetched;
    },
  }))
  .actions((self) => ({
    /**
     * Apply a dataset filter
     */
    setDatasetFilter: (datasetFilter: DatasetFilter) => {
      // only single selection is supported currently
      self.runInAction(() => {
        self.clearDatasetFilters();
        const hasDuplicate = self.datasetFilters.find(({ id, negate }) => id === datasetFilter.id && negate === datasetFilter.negate);
        if (!hasDuplicate) self.datasetFilters.push(datasetFilter);
      });
    },

    setShouldShowUnassigned: (showUnassigned: boolean) => {
      self.clearDatasetFilters();
      self.shouldShowUnassigned = showUnassigned;
    },

    getUnassignedFilter() {
      return self.unassignedFilter;
    },

    /**
     * Fetch all datasets in the app
     */
    fetchAllDatasets: async () => {
      const { env } = self;
      const { router } = Router;
      const { userOrOrgId, appId } = router?.query as { userOrOrgId: string; appId: string; searchId: string };

      self.setAllDatasetsFetched(false);

      self.setDatasets([]);

      self.fetchAllPages<Dataset>(async (page, perPage) => {
        const call = {
          userOrOrgId,
          appId,
          page,
          perPage,
          name: '',
        };
        const response = await env.grpc.listDatasets(call);
        const datasets = mapOr(response, (r) => r.getDatasetsList(), []);

        self.setDatasets(datasets);
        return datasets;
      });

      self.setAllDatasetsFetched(true);

      return self.datasets;
    },

    /**
     * Use text to search for a dataset. Since we load all datasets we can just filter everything locally.
     */
    searchDatasets: async (filterText: string) => {
      return self.datasets.filter((dataset) => dataset.getId().includes(filterText));
    },
  }))
  .actions((self) => ({
    /**
     * Create a new dataset with the given name
     */
    createDataset: async (newDatasetId: string) => {
      const { env } = self;
      const { router } = Router;
      const { userOrOrgId, appId } = router?.query as { userOrOrgId: string; appId: string; searchId: string };
      const call = {
        userOrOrgId,
        appId,
        datasetIds: [newDatasetId],
      };
      await env.grpc.postDatasets(call);
      const results = await self.fetchAllDatasets();
      const newDataset = results.find((dataset) => dataset.getId() === newDatasetId);

      // difficult to test, need to change listDatasets request mid test
      // istanbul ignore next
      if (newDataset) {
        self.setDatasetFilter({ id: newDataset.getId(), negate: false });
      }
    },
  }));
